import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type OpcaoLocalizacao = { _id: string; descricao: string };

// Ordem natural: "0902005" antes de "0902028", "Doca 2" antes de "Doca 10".
const ordenarLocalizacoes = <T extends { descricao: string }>(
  lista: T[],
) =>
  [...lista].sort((a, b) =>
    (a.descricao ?? "").localeCompare(b.descricao ?? "", "pt-BR", {
      numeric: true,
      sensitivity: "base",
    }),
  );

// Nível de localização com busca: a lista vem em ordem crescente e o
// operador pode digitar parte do nome para filtrar. `valorPor` diz se a tela
// guarda o _id ou a descrição da localização escolhida.
export function LocalizacaoCombobox({
  id,
  opcoes,
  value,
  onChange,
  disabled,
  placeholder,
  valorPor = "_id",
}: {
  id?: string;
  opcoes: OpcaoLocalizacao[] | undefined;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  valorPor?: "_id" | "descricao";
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ordenadas = useMemo(() => ordenarLocalizacoes(opcoes ?? []), [opcoes]);
  const selecionada = ordenadas.find((o) => o[valorPor] === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          {selecionada ? (
            <span className="truncate">{selecionada.descricao}</span>
          ) : (
            <span className="text-muted-foreground">
              {placeholder ?? t("common2.notDefined")}
            </span>
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-(--radix-popover-trigger-width)"
      >
        {/* O value do item é o _id (descrições podem repetir); a busca olha
            só a descrição, que vai em keywords. */}
        <Command
          className="rounded-lg"
          filter={(_value, search, keywords) =>
            (keywords?.[0] ?? "").toLowerCase().includes(search.toLowerCase())
              ? 1
              : 0
          }
        >
          <CommandInput placeholder="Buscar localização..." />
          <CommandList>
            <CommandEmpty>
              <div className="text-xs text-muted-foreground p-2">
                Nenhuma localização encontrada
              </div>
            </CommandEmpty>
            <CommandGroup>
              {ordenadas.map((o) => (
                <CommandItem
                  key={o._id}
                  value={o._id}
                  keywords={[o.descricao ?? ""]}
                  onSelect={() => {
                    onChange(o[valorPor]);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selecionada?._id === o._id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{o.descricao}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
